import { type ReactNode, useContext } from "react";
import {
  ArgusConnectionContext,
  type ArgusConnectionContextValue,
} from "../contexts/argus-connection-context";
import { useArgusConnection } from "../hooks/argus/use-argus-connection";

/**
 * Argus connection provider — always-mounted, sibling to `SupportChatProvider`.
 *
 * The provider owns shared UI state and exposes gateway actions so the full
 * `/argus` page and the sliding sidebar share the same SSE connection, event
 * buffer, and session state. Follows the same pattern as
 * `SupportChatProvider` (`new-fe/src/contexts/support-chat-context.tsx`) +
 * `useSupportChat` (`new-fe/src/hooks/use-support-chat.ts`).
 *
 * Phase 1: mounts the stub hook (no real SSE/gateway). Phase 2 wires the
 * real hooks without touching the provider itself.
 */
export const ArgusConnectionProvider = ({ children }: { children: ReactNode }) => {
  const value = useArgusConnection();
  return (
    <ArgusConnectionContext.Provider value={value}>{children}</ArgusConnectionContext.Provider>
  );
};

export const useArgusConnectionContext = (): ArgusConnectionContextValue => {
  const ctx = useContext(ArgusConnectionContext);
  if (!ctx) {
    throw new Error("useArgusConnectionContext must be used within an ArgusConnectionProvider");
  }
  return ctx;
};
